import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Slider } from 'primereact/slider';
import {
  dayjsFromApiUtcInTz,
  dayjsFromDayAndTimeStrings,
  stringDateTimeWithLocaleFromApiStringInTz,
  apiPragueFromDayjsInTz,
  apiUtcFromDayjs,
} from '@utils/dateWithTz';
import { isNotNullOrUndefined } from '@utils/utils';
import { Note } from '@components/styles';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import { RingilDialog } from '@components/obsolete/RingilDialog/RingilDialog.tsx';
import HorizontalFieldValue from '@components/obsolete/HorizontalFieldValue.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';

interface ComponentProps {
  localDate: string;
  sinceHourString: string;
  tillHourString: string;
  timeZone: string;
  utcStart: string;
  utcEnd: string;
  timezoneId: string;
  calendarId: string;
  reservationId: string;
  onComplete: () => void;
}

export const useCreateChangeSlotDuration = () => {
  const [visible, setVisible] = useState(false);

  const ReservationChangeSlotDuration: React.FC<ComponentProps> = ({ localDate, sinceHourString, tillHourString, timeZone, utcStart, utcEnd, timezoneId, calendarId, reservationId, onComplete }) => {
    const { tr } = useTranslationLgs();
    const { updateReservationTimeslotDuration } = useContext(ApiContext);

    const useNewFields = isNotNullOrUndefined(utcStart) && isNotNullOrUndefined(utcEnd) && isNotNullOrUndefined(timezoneId);

    const sinceDayjs = useNewFields
      ? dayjsFromApiUtcInTz(utcStart, timezoneId)
      : dayjsFromDayAndTimeStrings(localDate, sinceHourString, timeZone);
    const tillDayjs = useNewFields
      ? dayjsFromApiUtcInTz(utcEnd, timezoneId)
      : dayjsFromDayAndTimeStrings(localDate, tillHourString, timeZone);

    const effectiveTz = useNewFields ? timezoneId : timeZone;

    const max = tillDayjs.diff(sinceDayjs, 'minutes');
    const [durationInMinutes, setdurationInMinutes] = useState(max);

    const tillComputedDayjs = sinceDayjs.add(durationInMinutes, 'minutes');
    const tillComputedIso = tillComputedDayjs.toISOString();
    const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();

    const changeDuration = () => {
      updateReservationTimeslotDuration(reservationId, {
        since: apiPragueFromDayjsInTz(sinceDayjs, effectiveTz),
        till: apiPragueFromDayjsInTz(tillComputedDayjs, effectiveTz),
        calendarId,
        utcStart: useNewFields ? apiUtcFromDayjs(sinceDayjs) : undefined,
        utcEnd: useNewFields ? apiUtcFromDayjs(tillComputedDayjs) : undefined,
        timezoneId: useNewFields ? timezoneId : undefined,
      }, () => {
        setVisible(false);
        onComplete();
      });
    };

    return (
      <>
        <RingilDialog headerText={tr(`ChangeSlotSidebar.changeSlot`, `Change Slot`)} visible={visible} onHide={() => setVisible(false)} onSave={() => changeDuration()}>
          <BackendValidationComponent />
          <Grid>
            <HorizontalFieldValue label={tr(`ChangeSlotSidebar.from`, `From`)} value={stringDateTimeWithLocaleFromApiStringInTz(sinceDayjs.toISOString(), effectiveTz)} />
            <HorizontalFieldValue label={tr(`ChangeSlotSidebar.till`, `Till`)} value={stringDateTimeWithLocaleFromApiStringInTz(tillComputedIso, effectiveTz)} />
          </Grid>
          <HorizontalFieldValue
            label={tr(`ChangeSlotSidebar.duration`, `Duration`)}
            value={<Slider max={max} value={durationInMinutes} onChange={e => setdurationInMinutes(e.value as number)} step={1} />}
          />
          <Note>{tr(`ChangeSlotSidebar.changeTheValueByDraggingTheSlider`, `Change the value by dragging the slider`)}</Note>
          <COTimezone timezoneId={effectiveTz} />
        </RingilDialog>
      </>
    );
  };
  return { ReservationChangeSlotDuration, setVisible, visible };
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;
